import moment from 'moment-timezone';
import type React from 'react';
import { useCallback } from 'react';
import { useAppDispatch } from 'src/hooks/redux-hooks';
import { updateVisibility } from 'src/redux/modals/slice';
import type { TenantContactMessage } from 'src/types';

interface MediaMessageProps {
	message: TenantContactMessage;
	jobTimezone: string;
}

const VIDEO_PLACEHOLDER = '/svg/video-thumb-placeholder.svg';

const MediaMessage: React.FC<MediaMessageProps> = ({
	message,
	jobTimezone,
}) => {
	const dispatch = useAppDispatch();
	const isVideo = message.media_type === 'video';
	const thumb = isVideo
		? message.thumbnail_url || message.video_thumbnail_url || VIDEO_PLACEHOLDER
		: message.text;

	const openLightbox = useCallback(() => {
		dispatch(
			updateVisibility({
				isVisible: true,
				modalType: 'imageSlidesModal',
				extraProps: {
					modalData: {
						collection: [
							{
								url: message.text,
								thumbnail_url:
									message.thumbnail_url || message.video_thumbnail_url,
								media_type: isVideo ? 'video' : 'image',
								attachment_download_url: message.attachment_download_url,
							},
						],
						position: 0,
					},
				},
			}),
		);
	}, [
		dispatch,
		isVideo,
		message.attachment_download_url,
		message.text,
		message.thumbnail_url,
		message.video_thumbnail_url,
	]);

	const stamp =
		message.created_date_utc && jobTimezone
			? moment
					.utc(message.created_date_utc, 'YYYY-MM-DD HH:mm:ss')
					.tz(jobTimezone)
			: null;
	const time = stamp ? stamp.format('MMM D, YYYY - hh:mm A z') : '';
	const fromNow = stamp ? stamp.fromNow() : '';

	return (
		<div
			className={`tenant-texts__row tenant-texts__row--${message.direction} tenant-texts__row--media`}
		>
			<div
				className={`tenant-texts__media-card tenant-texts__media-card--${message.direction}`}
			>
				<div className="tenant-texts__media-header">
					<span className="tenant-texts__media-time">{time}</span>
					<span className="tenant-texts__media-from-now">{fromNow}</span>
				</div>
				<button
					type="button"
					className={`tenant-texts__media${isVideo ? ' tenant-texts__media--video' : ''}`}
					onClick={openLightbox}
				>
					{/* biome-ignore lint/performance/noImgElement: external CDN thumbnail, next/image not applicable */}
					<img
						className="tenant-texts__media-img"
						alt="media attachment"
						src={
							thumb ||
							'https://images.unsplash.com/photo-1707343843437-caacff5cfa74?q=80&w=400&auto=format&fit=crop'
						}
						onError={(e) => {
							e.currentTarget.src =
								'https://images.unsplash.com/photo-1707343843437-caacff5cfa74?q=80&w=400&auto=format&fit=crop';
						}}
					/>
				</button>
			</div>
		</div>
	);
};

export default MediaMessage;
